import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronRight, Building, Wallet, Users, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { mergeComparisonItems } from '../../utils/reportUtils';
import { useLegalForm } from '../legal-form/useLegalForm';

const BalanceSheetReport = ({
  reportData,
  comparisonData,
  showComparison,
  viewMode,
  formatAmount,
  renderVariance,
  showGlCodes,
  openDrilldown
}) => {
  const { data: legalForm } = useLegalForm();
  const regimeLabels = legalForm?.report_labels || {};
  const [expandedSections, setExpandedSections] = React.useState({
    currentAssets: true,
    nonCurrentAssets: true,
    currentLiabilities: true,
    nonCurrentLiabilities: true,
    equity: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Helper to calculate variance amount and percentage
  const calculateVariance = (current, previous, inverted = false) => {
    const currentVal = Number(current) || 0;
    const previousVal = Number(previous) || 0;
    const diff = currentVal - previousVal;
    const percentage = previousVal !== 0 ? ((diff / Math.abs(previousVal)) * 100) : (currentVal !== 0 ? 100 : 0);
    const isPositive = inverted ? diff < 0 : diff > 0;
    return { diff, percentage, isPositive };
  };

  // Helper to find comparison item by code or name
  const findComparisonItem = (item, comparisonArray) => {
    if (!comparisonArray || !Array.isArray(comparisonArray)) return null;
    return comparisonArray.find(c => 
      (c.code && item.code && c.code === item.code) || 
      (c.name && item.name && c.name === item.name)
    );
  };

  if (!reportData) return null;

  // Summary View
  if (viewMode === 'summary') {
    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Assets</p>
                  <div className="flex items-center">
                    <p className="text-base font-bold text-blue-600 break-all">{formatAmount(reportData.assets?.total)}</p>
                    {showComparison && comparisonData && renderVariance(reportData.assets?.total, comparisonData.assets?.total)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Liabilities</p>
                  <div className="flex items-center">
                    <p className="text-base font-bold text-orange-600 break-all">{formatAmount(reportData.liabilities?.total)}</p>
                    {showComparison && comparisonData && renderVariance(reportData.liabilities?.total, comparisonData.liabilities?.total, true)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Equity</p>
                  <div className="flex items-center">
                    <p className="text-base font-bold text-green-600 break-all">{formatAmount(reportData.equity?.total)}</p>
                    {showComparison && comparisonData && renderVariance(reportData.equity?.total, comparisonData.equity?.total)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comparison Table for Summary View */}
        {showComparison && comparisonData && (
          <Card data-testid="balance-sheet-comparison-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Period Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Current Period</TableHead>
                    <TableHead className="text-right">Comparison Period</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-right">Variance %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { label: 'Current Assets', current: reportData.assets?.current?.total, previous: comparisonData.assets?.current?.total, inverted: false },
                    { label: 'Non-Current Assets', current: reportData.assets?.non_current?.total, previous: comparisonData.assets?.non_current?.total, inverted: false },
                    { label: 'Total Assets', current: reportData.assets?.total, previous: comparisonData.assets?.total, inverted: false, isTotal: true },
                    { label: 'Current Liabilities', current: reportData.liabilities?.current?.total, previous: comparisonData.liabilities?.current?.total, inverted: true },
                    { label: 'Non-Current Liabilities', current: reportData.liabilities?.non_current?.total, previous: comparisonData.liabilities?.non_current?.total, inverted: true },
                    { label: 'Total Liabilities', current: reportData.liabilities?.total, previous: comparisonData.liabilities?.total, inverted: true, isTotal: true },
                    { label: 'Total Equity', current: reportData.equity?.total, previous: comparisonData.equity?.total, inverted: false, isTotal: true },
                  ].map((row, idx) => {
                    const variance = calculateVariance(row.current, row.previous, row.inverted);
                    return (
                      <TableRow key={idx} className={row.isTotal ? 'font-bold bg-muted/30' : ''}>
                        <TableCell className={row.isTotal ? 'font-semibold' : ''}>{row.label}</TableCell>
                        <TableCell className="text-right font-mono">{formatAmount(row.current)}</TableCell>
                        <TableCell className="text-right font-mono">{formatAmount(row.previous)}</TableCell>
                        <TableCell className={`text-right font-mono ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                          {variance.diff >= 0 ? '+' : ''}{formatAmount(variance.diff)}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                          <span className="flex items-center justify-end gap-1">
                            {variance.diff !== 0 && (variance.isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />)}
                            {variance.percentage.toFixed(1)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Accounting Equation Check */}
        <Card className="bg-muted/30">
          <CardContent className="py-4">
            <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
              <span className="font-medium">Assets</span>
              <span className="font-bold text-blue-600 break-all">{formatAmount(reportData.assets?.total)}</span>
              <span className="text-muted-foreground">=</span>
              <span className="font-medium">Liabilities</span>
              <span className="font-bold text-orange-600 break-all">{formatAmount(reportData.liabilities?.total)}</span>
              <span className="text-muted-foreground">+</span>
              <span className="font-medium">Equity</span>
              <span className="font-bold text-green-600 break-all">{formatAmount(reportData.equity?.total)}</span>
              {Math.abs((reportData.assets?.total || 0) - ((reportData.liabilities?.total || 0) + (reportData.equity?.total || 0))) < 1 ? (
                <Badge variant="default" className="ml-2">Balanced</Badge>
              ) : (
                <Badge variant="destructive" className="ml-2">Out of Balance</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Helper to render detailed section with comparison columns
  const renderDetailedSection = (title, items, comparisonItems, color, sectionKey, totalLabel, totalAmount, comparisonTotal, inverted = false) => {
    return (
      <Card className="mb-3">
        <CardHeader 
          className="cursor-pointer hover:bg-muted/50 transition-colors py-3"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {expandedSections[sectionKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              {title}
            </CardTitle>
            <span className={`font-bold ${color}`}>{formatAmount(totalAmount)}</span>
          </div>
        </CardHeader>
        <AnimatePresence>
          {expandedSections[sectionKey] && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <CardContent className="pt-0">
                {showComparison && comparisonData ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        {showGlCodes && <TableHead className="w-[80px]">Code</TableHead>}
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right w-[120px]">Current</TableHead>
                        <TableHead className="text-right w-[120px]">Previous</TableHead>
                        <TableHead className="text-right w-[100px]">Variance</TableHead>
                        <TableHead className="text-right w-[80px]">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mergeComparisonItems(items, comparisonItems).map((item, idx) => {
                        const compItem = findComparisonItem(item, comparisonItems);
                        const compAmount = compItem?.balance || compItem?.amount || 0;
                        const variance = calculateVariance(item.balance || item.amount, compAmount, inverted);
                        return (
                          <TableRow 
                            key={idx} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => openDrilldown(item)}
                          >
                            {showGlCodes && <TableCell className="text-muted-foreground font-mono text-xs">{item.code}</TableCell>}
                            <TableCell className="text-sm">{item.name}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{formatAmount(item.balance || item.amount)}</TableCell>
                            <TableCell className="text-right font-mono text-sm text-muted-foreground">{formatAmount(compAmount)}</TableCell>
                            <TableCell className={`text-right font-mono text-sm ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                              {variance.diff >= 0 ? '+' : ''}{formatAmount(variance.diff)}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-xs ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                              {variance.percentage.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {(!items || items.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={showGlCodes ? 6 : 5} className="text-center text-muted-foreground py-4">
                            No items recorded
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="space-y-1">
                    {items?.map((item, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between py-2 px-2 hover:bg-muted/50 rounded cursor-pointer transition-colors"
                        onClick={() => openDrilldown(item)}
                      >
                        <span className="text-sm text-foreground">
                          {showGlCodes && item.code && <span className="text-muted-foreground mr-2">{item.code}</span>}
                          {item.name}
                        </span>
                        <span className="font-medium">{formatAmount(item.balance || item.amount)}</span>
                      </div>
                    ))}
                    {(!items || items.length === 0) && (
                      <p className="text-sm text-muted-foreground py-2">No items recorded</p>
                    )}
                  </div>
                )}
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    );
  };

  // Detailed View
  return (
    <div className="space-y-6" data-testid="balance-sheet-detailed-view">
      {/* ASSETS */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
          <Building className="w-5 h-5 text-blue-600" />
          Assets
        </h3>

        {/* Current Assets */}
        {renderDetailedSection(
          'Current Assets',
          reportData.assets?.current?.items,
          comparisonData?.assets?.current?.items,
          'text-blue-600',
          'currentAssets',
          'Total Current Assets',
          reportData.assets?.current?.total,
          comparisonData?.assets?.current?.total,
          false
        )}

        {/* Non-Current Assets */}
        {renderDetailedSection(
          'Non-Current Assets',
          reportData.assets?.non_current?.items,
          comparisonData?.assets?.non_current?.items,
          'text-blue-600',
          'nonCurrentAssets',
          'Total Non-Current Assets',
          reportData.assets?.non_current?.total,
          comparisonData?.assets?.non_current?.total,
          false
        )}

        {/* Total Assets */}
        <Card className="bg-blue-50/50 border-blue-200">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <span className="font-bold text-blue-700">Total Assets</span>
              <div className="flex items-center gap-4">
                <span className="font-bold text-xl text-blue-700">{formatAmount(reportData.assets?.total)}</span>
                {showComparison && comparisonData && (
                  <>
                    <span className="text-muted-foreground text-sm">vs</span>
                    <span className="text-muted-foreground">{formatAmount(comparisonData.assets?.total)}</span>
                    {(() => {
                      const v = calculateVariance(reportData.assets?.total, comparisonData.assets?.total, false);
                      return (
                        <span className={`font-medium ${v.isPositive ? 'text-green-600' : v.diff !== 0 ? 'text-red-500' : ''}`}>
                          ({v.diff >= 0 ? '+' : ''}{v.percentage.toFixed(1)}%)
                        </span>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* LIABILITIES */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-orange-600" />
          Liabilities
        </h3>

        {/* Current Liabilities */}
        {renderDetailedSection(
          'Current Liabilities',
          reportData.liabilities?.current?.items,
          comparisonData?.liabilities?.current?.items,
          'text-orange-600',
          'currentLiabilities',
          'Total Current Liabilities',
          reportData.liabilities?.current?.total,
          comparisonData?.liabilities?.current?.total,
          true
        )}

        {/* Non-Current Liabilities */}
        {renderDetailedSection(
          'Non-Current Liabilities',
          reportData.liabilities?.non_current?.items,
          comparisonData?.liabilities?.non_current?.items,
          'text-orange-600',
          'nonCurrentLiabilities',
          'Total Non-Current Liabilities',
          reportData.liabilities?.non_current?.total,
          comparisonData?.liabilities?.non_current?.total,
          true
        )}

        {/* Total Liabilities */}
        <Card className="bg-orange-50/50 border-orange-200">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <span className="font-bold text-orange-700">Total Liabilities</span>
              <div className="flex items-center gap-4">
                <span className="font-bold text-xl text-orange-700">{formatAmount(reportData.liabilities?.total)}</span>
                {showComparison && comparisonData && (
                  <>
                    <span className="text-muted-foreground text-sm">vs</span>
                    <span className="text-muted-foreground">{formatAmount(comparisonData.liabilities?.total)}</span>
                    {(() => {
                      const v = calculateVariance(reportData.liabilities?.total, comparisonData.liabilities?.total, true);
                      return (
                        <span className={`font-medium ${v.isPositive ? 'text-green-600' : v.diff !== 0 ? 'text-red-500' : ''}`}>
                          ({v.diff >= 0 ? '+' : ''}{v.percentage.toFixed(1)}%)
                        </span>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* EQUITY */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-green-600" />
          <span data-testid="bs-equity-section-title">{regimeLabels.equity_section || 'Equity'}</span>
        </h3>

        {/* Equity Components - Show all equity GL accounts */}
        {renderDetailedSection(
          'Equity Components',
          reportData.equity?.items?.filter(item => item.code !== '3110') || [],
          comparisonData?.equity?.items?.filter(item => item.code !== '3110'),
          'text-green-600',
          'equity',
          'Subtotal Equity Accounts',
          (reportData.equity?.items?.filter(item => item.code !== '3110') || []).reduce((sum, item) => sum + (item.balance || 0), 0),
          (comparisonData?.equity?.items?.filter(item => item.code !== '3110') || []).reduce((sum, item) => sum + (item.balance || 0), 0),
          false
        )}

        {/* Retained Earnings Section - Always show */}
        <Card className="mb-3">
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Badge variant="outline" className="bg-green-50 text-green-700">3100</Badge>
                <span data-testid="bs-retained-earnings-label">{regimeLabels.retained_earnings || 'Retained Earnings'}</span>
              </span>
              <span className="font-bold text-green-600">
                {formatAmount((reportData.equity?.summary?.total_retained_earnings) || reportData.equity?.retained_earnings_current_year || 0)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2 pl-4 border-l-2 border-green-200">
              {/* Accumulated Retained Earnings */}
              <div className="flex items-center justify-between py-2 px-2 hover:bg-muted/50 rounded">
                <span className="text-sm text-muted-foreground">
                  Accumulated Retained Earnings (Prior Years)
                </span>
                <span className="font-medium">
                  {formatAmount(reportData.equity?.summary?.accumulated_retained_earnings || 0)}
                </span>
              </div>
              
              {/* Current Year Earnings */}
              <div className="flex items-center justify-between py-2 px-2 hover:bg-muted/50 rounded bg-green-50/50">
                <span className="text-sm">
                  <Badge variant="outline" className="mr-2 text-xs">3110</Badge>
                  Current Year Earnings (Profit/Loss)
                </span>
                <span className={`font-medium ${(reportData.equity?.retained_earnings_current_year || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {formatAmount(reportData.equity?.retained_earnings_current_year || reportData.equity?.summary?.current_year_earnings || 0)}
                </span>
              </div>
              
              {/* Total Retained Earnings */}
              <div className="flex items-center justify-between py-2 px-2 bg-green-100/50 rounded font-medium">
                <span className="text-sm">Total Retained Earnings</span>
                <span className="text-green-700">
                  {formatAmount(
                    (reportData.equity?.summary?.accumulated_retained_earnings || 0) + 
                    (reportData.equity?.retained_earnings_current_year || reportData.equity?.summary?.current_year_earnings || 0)
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Equity */}
        <Card className="bg-green-50/50 border-green-200">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <span className="font-bold text-green-700">Total Equity</span>
              <div className="flex items-center gap-4">
                <span className="font-bold text-xl text-green-700">{formatAmount(reportData.equity?.total)}</span>
                {showComparison && comparisonData && (
                  <>
                    <span className="text-muted-foreground text-sm">vs</span>
                    <span className="text-muted-foreground">{formatAmount(comparisonData.equity?.total)}</span>
                    {(() => {
                      const v = calculateVariance(reportData.equity?.total, comparisonData.equity?.total, false);
                      return (
                        <span className={`font-medium ${v.isPositive ? 'text-green-600' : v.diff !== 0 ? 'text-red-500' : ''}`}>
                          ({v.diff >= 0 ? '+' : ''}{v.percentage.toFixed(1)}%)
                        </span>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total Liabilities + Equity */}
      <Card className="bg-gradient-to-r from-orange-50 to-green-50 border-2">
        <CardContent className="py-4 px-4">
          <div className="flex items-center justify-between">
            <span className="font-bold text-lg text-foreground">Total Liabilities + Equity</span>
            <span className="font-bold text-xl text-foreground">
              {formatAmount((reportData.liabilities?.total || 0) + (reportData.equity?.total || 0))}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BalanceSheetReport;
